import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { label: "Features", href: "#features" },
    { label: "Technology", href: "#technology" },
    { label: "Trainers", href: "#trainers" },
    { label: "Pricing", href: "#pricing" },
  ];

  // Handle scroll effects
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      // Determine active section based on scroll position
      const sections = ["features", "technology", "trainers", "pricing"];
      let current = "";
      
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 150 && rect.bottom >= 150) {
            current = section;
            break;
          }
        }
      }
      setActiveSection(current);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Smooth scroll to section
  const scrollToSection = (e, href) => {
    e.preventDefault();
    
    if (href === "#") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      setMobileMenuOpen(false);
      return;
    }
    
    const id = href.replace("#", "");
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
    setMobileMenuOpen(false);
  };

  // Check if nav item is active
  const isActive = (href) => {
    return activeSection === href.replace("#", "");
  };

  return (
    <>
      {/* Navigation Bar */}
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? "bg-black/90 backdrop-blur-xl border-b border-white/10" 
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <a 
              href="#"
              onClick={(e) => scrollToSection(e, "#")}
              className="flex-shrink-0 transition-transform hover:scale-105 active:scale-95"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_evolve-fitness-5/artifacts/uqt72vx4_IMG_1561.png"
                alt="EVOLVE"
                className="h-12 w-auto"
              />
            </a>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-2">
              {navItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={(e) => scrollToSection(e, item.href)}
                  className={`relative px-5 py-2.5 text-sm font-medium transition-all duration-300 rounded-full group ${
                    isActive(item.href)
                      ? "text-[#00FFD1]"
                      : "text-gray-300 hover:text-white"
                  }`}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <span className="relative z-10">{item.label}</span>
                  
                  {/* Background highlight on hover/active */}
                  <span className={`absolute inset-0 rounded-full transition-all duration-300 ${
                    isActive(item.href) 
                      ? "bg-[#00FFD1]/10" 
                      : "bg-transparent group-hover:bg-white/5"
                  }`} />
                  
                  {/* Underline indicator */}
                  <span className={`absolute bottom-1 left-1/2 -translate-x-1/2 h-0.5 bg-[#00FFD1] rounded-full transition-all duration-300 ${
                    isActive(item.href) ? "w-6" : "w-0 group-hover:w-4"
                  }`} />
                </a>
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden md:block">
              <motion.a
                href="#features"
                onClick={(e) => scrollToSection(e, "#features")}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="inline-flex items-center px-6 py-2.5 bg-[#00FFD1] text-black text-sm font-bold rounded-full hover:bg-[#00FFD1]/90 transition-all shadow-[0_0_20px_rgba(0,255,209,0.3)] hover:shadow-[0_0_30px_rgba(0,255,209,0.5)]"
                data-testid="nav-cta"
              >
                Get Started
              </motion.a>
            </div>

            {/* Mobile Menu Button */}
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-white hover:text-[#00FFD1] transition-colors rounded-lg hover:bg-white/5 active:bg-white/10"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </motion.button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-40 md:hidden"
        >
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/90 backdrop-blur-md"
            onClick={() => setMobileMenuOpen(false)}
          />
          
          {/* Menu Content */}
          <motion.div 
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="absolute top-20 left-0 right-0 bg-[#0A0A0A] border-b border-white/10 px-6 py-6"
          >
            <div className="space-y-1">
              {navItems.map((item, index) => (
                <motion.a
                  key={item.href}
                  href={item.href}
                  onClick={(e) => scrollToSection(e, item.href)}
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: index * 0.05 }}
                  className={`block px-4 py-3 rounded-xl font-medium transition-all active:scale-98 ${
                    isActive(item.href)
                      ? "text-[#00FFD1] bg-[#00FFD1]/10"
                      : "text-white hover:bg-white/5 active:bg-white/10"
                  }`}
                >
                  {item.label}
                </motion.a>
              ))}
            </div>

            {/* Mobile CTA */}
            <motion.a
              href="#features"
              onClick={(e) => scrollToSection(e, "#features")}
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              whileTap={{ scale: 0.98 }}
              className="block mt-4 py-3 bg-[#00FFD1] text-black font-bold rounded-xl text-center active:bg-[#00FFD1]/90"
            >
              Get Started
            </motion.a>
          </motion.div>
        </motion.div>
      )}
    </>
  );
};

export default Navigation;
